import { AutoCompleteOption } from 'scenes/Dashboard/components/Inputs/Select/AutoComplete/AutoComplete.model'
import { DateTrustCode } from 'scenes/Dashboard/data/common/types'
import { Education, Person, Position } from 'scenes/Dashboard/data/people/types'

export type PersonFormValues = {
  id: Person['id']
  firstName: Person['firstName']
  lastName: Person['lastName']
  gender: Person['gender']
  email: Person['email']
  emailValidationStatus: Person['emailValidationStatus']
  bornOn: string
  diedOn: string
  roles: PositionFormValues[]
  educations: EducationFormValues[]
  blurb: string
  description: string
  linkedinUrl: string
}

export type PositionFormValues = {
  id: Position['id']
  organization: AutoCompleteOption | ''
  title: Position['title']
  description: Position['description']
  startedOn: Position['startedOn']
  endedOn: Position['endedOn']
  toDelete: Position['toDelete']
  isCurrent: Position['isCurrent']
}

export type EducationFormValues = {
  id: Education['id']
  organization: AutoCompleteOption | ''
  degreeType: Education['degreeType']
  degreeSubject: Education['degreeSubject']
  grade: Education['grade']
  startedOn: Education['startedOn']
  endedOn: Education['endedOn']
  toDelete: Education['toDelete']
}

export function mapPositionToFormValues({
  id,
  organization,
  title = '',
  description = '',
  startedOn = '',
  endedOn = '',
  toDelete = false,
  isCurrent = false,
}: Position): PositionFormValues {
  return {
    id,
    // TODO: fix types
    organization: organization || '',
    title,
    description: description || '',
    startedOn,
    endedOn,
    toDelete,
    isCurrent,
  }
}

export function mapEducationToFormValues({
  id,
  organization,
  degreeType = '',
  degreeSubject = '',
  grade = '',
  startedOn = '',
  endedOn = '',
  toDelete = false,
}: Education): EducationFormValues {
  return {
    id,
    // TODO: fix types
    organization: organization || '',
    degreeType,
    degreeSubject: degreeSubject || '',
    grade: grade || '',
    startedOn,
    endedOn,
    toDelete,
  }
}

export function mapPersonToFormValues({
  id,
  firstName,
  lastName,
  gender,
  email,
  emailValidationStatus,
  bornOn,
  diedOn,
  roles,
  educations,
  blurb,
  description,
  linkedinUrl,
}: Person): PersonFormValues {
  return {
    id,
    firstName: firstName || '',
    lastName: lastName || '',
    gender: gender || '',
    email: email || '',
    emailValidationStatus: emailValidationStatus || '',
    bornOn: bornOn || '',
    diedOn: diedOn || '',
    roles: roles ? roles.map(mapPositionToFormValues) : [],
    educations: educations ? educations.map(mapEducationToFormValues) : [],
    blurb: blurb || '',
    description: description || '',
    linkedinUrl: linkedinUrl || '',
  }
}

export function mapFormValuesToPerson({
  id,
  roles,
  educations,
  gender,
  bornOn,
  diedOn,
  ...personData
}: PersonFormValues): Person {
  return {
    ...personData,
    id,
    bornOn: bornOn || '',
    bornOnTrustCode: bornOn ? DateTrustCode.yearDayMonth : '',
    diedOn: diedOn || '',
    diedOnTrustCode: diedOn ? DateTrustCode.yearDayMonth : '',
    gender: gender || '',
    roles: roles.map((position) => mapFormValuesToPosition(position, id)),
    educations: educations.map((education) => mapFormValuesToEducation(education, id)),
  }
}

export function mapFormValuesToPosition(
  { organization, startedOn, endedOn, ...positionData }: PositionFormValues,
  personId: number
): Position {
  return {
    ...positionData,
    personId,
    startedOn: startedOn || '',
    startedOnTrustCode: startedOn ? DateTrustCode.yearMonth : DateTrustCode.null,
    endedOn: endedOn || '',
    endedOnTrustCode: endedOn ? DateTrustCode.yearMonth : DateTrustCode.null,
    organization: !!organization ? organization : null,
    organizationId: !!organization ? organization.value.id : null,
  }
}

export function mapFormValuesToEducation(
  { organization, startedOn, endedOn, ...educationData }: EducationFormValues,
  personId: number
): Education {
  return {
    ...educationData,
    personId,
    startedOn: startedOn || '',
    startedOnTrustCode: startedOn ? DateTrustCode.yearMonth : DateTrustCode.null,
    endedOn: endedOn || '',
    endedOnTrustCode: endedOn ? DateTrustCode.yearMonth : DateTrustCode.null,
    organization: !!organization ? organization : null,
    organizationId: !!organization ? organization.value.id : null,
  }
}
